import type { MediaSize } from './types';
import type { RenderFlags } from './frameByFrameRenderer/Renderer';
import { FrameByFrameRenderer } from './frameByFrameRenderer/Renderer';
import { VideoFrameExtractor } from './frameByFrameRenderer/frameExtractors/videoFrameExtractor';
import type { OpaqueVideoData } from './video-data/types';
import { OfflineAudioEncoder } from './encoders/OfflineAudioEncoder';
import { VideoWebcodecsWebmRecorder } from './videoWebCodecsRecorder';

type RenderSlideOptions = {
    resolutionValue: number | null;
    mediaSize?: MediaSize;
    startTime: number;
    endTime: number;
    videoData: OpaqueVideoData;
    flags: RenderFlags;
    /**
     * allows to show the rendering process in the nwjs window for debugging purposes
     */
    canvasParent?: HTMLElement;
};

/**
 * Render a slice of the given video data to webm buffer
 */
export const renderWebm = async ({
    videoData,
    startTime,
    endTime,
    resolutionValue,
    mediaSize,
    flags,
    canvasParent,
}: RenderSlideOptions): Promise<ArrayBuffer> => {
    const audioEncoder = new OfflineAudioEncoder({ flags });
    audioEncoder.setStartTime(startTime);
    audioEncoder.setStopTime(endTime);
    audioEncoder.luminary.load(videoData);
    await audioEncoder.encode();

    const recorder = new VideoWebcodecsWebmRecorder({
        audioBuffer: audioEncoder.audioBuffer,
    });

    await audioEncoder.destroy();

    const frameExtractor = new VideoFrameExtractor();

    const renderer = new FrameByFrameRenderer({
        videoData,
        startTime,
        endTime,
        resolutionValue,
        mediaSize,
        pipeline: { recorder, frameExtractor },
        flags,
        canvasParent,
    });

    await renderer.render();

    return recorder.webmBuffer;
};
